// File: /var/www/app2/frontend/src/App.tsx

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Reports from "./components/Reports";
import ReportDetail from "./components/ReportDetail";
import MissionaryManagement from "./components/MissionaryManagement";
import UserManagement from "./components/UserManagement";
import SupporterManagement from "./components/SupporterManagement";
import OrganizationManagement from "./components/OrganizationManagement";
import SupportRequests from "./components/SupportRequests";
import SecurityDashboard from "./components/SecurityDashboard";
import MediaLibrary from "./components/MediaLibrary";
import axios from "axios";
import "./App.css";

// User interface matching backend SessionUser type
export interface User {
  id: number;
  username: string;
  role: "superadmin" | "admin" | "supporter";
  fullName: string;
  firstName: string;
  lastName: string;
  organization?: string;
  email?: string;
}

// Configure axios defaults
axios.defaults.baseURL =
  process.env.NODE_ENV === "production"
    ? "" // Empty string means same domain in production
    : "http://localhost:3002";
axios.defaults.withCredentials = true; // CRITICAL: This ensures cookies are sent

// Add request interceptor for debugging
axios.interceptors.request.use(
  (config) => {
    console.log("Making request to:", config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login on 401
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("🔄 App mounted, checking auth...");
    checkAuth();
  }, []);

  useEffect(() => {
    console.log("👤 User state changed:", user);
  }, [user]);

  const checkAuth = async () => {
    try {
      console.log("🔍 Checking authentication...");
      const response = await axios.get("/api/auth/check");
      console.log("Auth check response:", response.data);
      // The backend wraps the response in a "data" property via the success() helper
      const authData = response.data.data || response.data;
      if (authData.authenticated) {
        console.log("✅ User authenticated:", authData.user);
        setUser(authData.user);
      } else {
        console.log("❌ User not authenticated");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData: User) => {
    console.log("User logged in:", userData);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post("/api/auth/logout");
      setUser(null);
      window.location.href = "/login"; // Hard redirect to clear any cached state
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    console.log("⏳ Loading...");
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        {user && (
          <nav className="navbar">
            <div className="nav-container">
              <h1>USA Office Report Management</h1>
              <div className="nav-items">
                <span className="user-info">
                  {user.fullName || user.username} ({user.role})
                  {user.organization && user.role === "supporter" && (
                    <span> - {user.organization}</span>
                  )}
                </span>
                <Link to="/" className="nav-link">
                  Dashboard
                </Link>
                <Link to="/reports" className="nav-link">
                  Reports
                </Link>
                <Link to="/missionaries" className="nav-link">
                  Missionaries
                </Link>
                {(user.role === "admin" || user.role === "superadmin") && (
                  <>
                    <Link to="/organizations" className="nav-link">
                      Organizations
                    </Link>
                    <Link to="/users" className="nav-link">
                      Users
                    </Link>
                    <Link to="/supporters" className="nav-link">
                      Supporters
                    </Link>
                    <Link to="/support-requests" className="nav-link">
                      Support Requests
                    </Link>
                    <Link to="/media" className="nav-link">
                      Media Library
                    </Link>
                  </>
                )}
                {user.role === "superadmin" && (
                  <Link to="/security" className="nav-link">
                    Security
                  </Link>
                )}
                <button onClick={handleLogout} className="logout-btn">
                  Logout
                </button>
              </div>
            </div>
          </nav>
        )}

        <Routes>
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />
            }
          />

          <Route
            path="/"
            element={
              !user ? <Navigate to="/login" /> : <Dashboard user={user} />
            }
          />

          <Route
            path="/reports"
            element={!user ? <Navigate to="/login" /> : <Reports user={user} />}
          />

          <Route
            path="/reports/:id"
            element={
              !user ? <Navigate to="/login" /> : <ReportDetail user={user} />
            }
          />

          <Route
            path="/missionaries"
            element={
              !user ? (
                (() => {
                  console.log("🚫 /missionaries - No user, redirecting to login");
                  return <Navigate to="/login" />;
                })()
              ) : (
                (() => {
                  console.log("✅ /missionaries - User authenticated, rendering MissionaryManagement");
                  return <MissionaryManagement user={user} />;
                })()
              )
            }
          />

          <Route
            path="/users"
            element={
              !user ? <Navigate to="/login" /> : <UserManagement user={user} />
            }
          />

          <Route
            path="/supporters"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SupporterManagement user={user} />
              )
            }
          />

          <Route
            path="/organizations"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <OrganizationManagement user={user} />
              )
            }
          />

          <Route
            path="/support-requests"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SupportRequests user={user} />
              )
            }
          />

          <Route
            path="/media"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : user.role === "admin" || user.role === "superadmin" ? (
                <MediaLibrary user={user} />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/security"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : (
                <SecurityDashboard user={user} />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
